// Live replay controller (REP-1/4). Bridges the running game to the pure replay
// engine. Recording is OPT-IN via the `recordReplay` gamerule — off by default, so
// the parity path never runs the extra canonical sim. When on, each tick it appends
// the player's consumed input (movement + any block edits that tick) to a canonical
// `Recorder` seeded from a snapshot of the CURRENT world, shows the "● REC" dot, and
// can pop the recording — or just its last `REP_INSTANT_SECONDS` — into the viewer.
//
// Only PLAYER-initiated input is recorded (movement + direct place/break); every
// world-sim edit (redstone, fluids, mob actions) is reproduced by the canonical
// session's own `runTick`, so it must NOT be recorded (that would double-apply). The
// replay therefore reproduces the deterministic §2 substrate, not the boot tick's
// non-recorded subsystems — see docs/decisions.md M33.
import { REP, TICK } from '../../sim/config';
import { Recorder } from '../../sim/replay/recorder';
import { ReplayPlayer } from '../../sim/replay/player';
import { RepBtn, RepFlag, type EditOp, type InputFrame, type Replay, type Snapshot } from '../../sim/replay/format';
import type { SessionInit } from '../../sim/replay/session';
import type { Game } from '../boot';
import type { ReplayOverlay } from './overlay';
import type { ReplayWorldView } from '../render/replayview';

export class ReplayController {
  private recorder: Recorder | null = null;
  private pendingEdits: EditOp[] = [];
  private lastReplay: Replay | null = null;

  constructor(
    private readonly game: Game,
    private readonly overlay: ReplayOverlay,
    private readonly view: ReplayWorldView | null = null,
  ) {}

  /** 3D re-render state (REP-3): whether the viewer is drawing the replay world, the
   *  resident chunk-mesh count, and the spectator camera pose (debug API / E2E). */
  view3D(): ReturnType<ReplayWorldView['state']> {
    return this.view?.state() ?? { active: false, meshes: 0, tick: -1, camera: { x: 0, y: 0, z: 0, yaw: 0, pitch: 0 } };
  }

  /** Whether a live recording is currently running. */
  get recording(): boolean {
    return this.recorder !== null;
  }

  /** Called once per game tick. Starts/stops recording from the gamerule and, while
   *  on, records the frame the player just drove (movement + this tick's edits). */
  observeLiveTick(): void {
    const on = this.game.world.gamerules.get('recordReplay') === 'true';
    if (!on) {
      if (this.recorder) this.stop();
      this.pendingEdits = [];
      return;
    }
    if (!this.recorder) this.start();
    this.recorder!.record(this.frameOf());
    this.pendingEdits = [];
    this.overlay.setRecording(true);
  }

  /** Record a player-initiated block edit for the current tick (place/break). */
  noteEdit(x: number, y: number, z: number, state: number, place: boolean): void {
    if (this.recorder) this.pendingEdits.push({ x, y, z, state, place });
  }

  /** Begin a recording anchored to a snapshot of the current world. */
  private start(): void {
    this.recorder = new Recorder(this.game.registries, this.sessionInit());
    this.overlay.setRecording(true);
  }

  /** Finish + retain the recording (the viewer/export source). */
  private stop(): void {
    this.lastReplay = this.recorder!.finish();
    this.recorder = null;
    this.overlay.setRecording(false);
  }

  /** The recording so far (a live snapshot), or the last finished one. */
  currentReplay(): Replay | null {
    return this.recorder ? this.recorder.finish() : this.lastReplay;
  }

  /** Open the viewer on the whole recording (F-key / menu). */
  openViewer(): boolean {
    return this.openReplay(this.currentReplay());
  }

  /** Hangar §39.5 (M105.1) — open a replay from OUTSIDE this session (a received
   *  `.bbreplay` file), not the live recorder's own. Same viewer, same rule: no empty replay. */
  openReplay(rep: Replay | null): boolean {
    if (!rep || rep.frames.length === 0) return false;
    this.overlay.mount();
    this.overlay.open(new ReplayPlayer(this.game.registries, rep));
    return true;
  }

  /** Instant replay (REP-4): open the viewer and jump to the last
   *  `REP_INSTANT_SECONDS` of the recording. */
  openInstantReplay(): boolean {
    const rep = this.currentReplay();
    if (!rep || rep.frames.length === 0) return false;
    this.overlay.mount();
    const player = new ReplayPlayer(this.game.registries, rep);
    const window = Math.round(REP.INSTANT_SECONDS * TICK.TPS);
    player.seek(Math.max(player.startTick, player.endTick - window));
    this.overlay.open(player);
    return true;
  }

  /** Build the canonical InputFrame the player drove this tick. */
  private frameOf(): InputFrame {
    const i = this.game.input;
    let buttons = 0;
    if (i.forward) buttons |= RepBtn.Forward;
    if (i.back) buttons |= RepBtn.Back;
    if (i.left) buttons |= RepBtn.Left;
    if (i.right) buttons |= RepBtn.Right;
    if (i.jump) buttons |= RepBtn.Jump;
    if (i.sneak) buttons |= RepBtn.Sneak;
    if (i.sprint) buttons |= RepBtn.Sprint;
    const flags = this.game.player.body.flying ? RepFlag.Flying : 0;
    return {
      buttons, yaw: this.game.player.yaw, pitch: this.game.player.pitch, flags,
      edits: this.pendingEdits, commands: [],
    };
  }

  /** SessionInit seeded from a snapshot of the CURRENT world so the recording starts
   *  from exactly what the player sees (works for any world type). */
  private sessionInit(): SessionInit {
    const meta = this.game.meta;
    const p = this.game.player;
    return {
      seed: String(meta.seed),
      options: { flat: meta.worldType === 'flat', flatPreset: meta.flatPreset ?? '', dimension: 'overworld' },
      spawn: { x: p.body.x, y: p.body.y, z: p.body.z, yaw: p.yaw, pitch: p.pitch },
      snapshot: liveSnapshot(this.game),
    };
  }
}

/** Capture the live world (clock + resident chunk coords + edited sections + player)
 *  as a replay Snapshot — the recording's start state. */
function liveSnapshot(game: Game): Snapshot {
  const w = game.world;
  const b = game.player.body;
  return {
    tickCount: w.tickCount,
    dayTime: w.dayTime,
    weather: {
      raining: w.weather.raining, thundering: w.weather.thundering,
      rainCountdown: w.weather.rainCountdown, thunderCountdown: w.weather.thunderCountdown,
    },
    player: { x: b.x, y: b.y, z: b.z, vx: b.vx, vy: b.vy, vz: b.vz, yaw: game.player.yaw, pitch: game.player.pitch },
    loadedChunks: [...w.loadedChunks()].map((c) => ({ cx: c.cx, cz: c.cz })),
    sections: w.getDirtySections().map((s) => ({ cx: s.cx, cz: s.cz, sy: s.sy, states: s.states ? Array.from(s.states) : null })),
  };
}
